import { z } from "zod";

export const shiftHandoverDefinitionSchema = z.object({
    title: z.string().trim().min(1).max(120),
    instructions: z.string().trim().min(1).max(6000),
    lead_minutes: z.number().int().min(0).max(1440),
    lookback_hours: z.number().int().min(1).max(168),
    timezone: z.string().min(1).refine(value => {
        try { new Intl.DateTimeFormat("en", { timeZone: value }); return true; } catch { return false; }
    }, "Choose a valid time zone"),
    recipient_member_ids: z.array(z.string().min(1).max(200).regex(/^\S+$/)).max(1000).refine(ids => new Set(ids).size === ids.length, "Choose distinct recipients"),
    language: z.string().min(2).max(20)
});
export const shiftHandoverRuleSchema = z.object({
    id: z.string().min(1), definition: shiftHandoverDefinitionSchema,
    revision: z.number().int().positive(), enabled: z.boolean(), created_at: z.string(), updated_at: z.string()
});
export const handoverShiftSchema = z.object({
    employee_name: z.string(), date: z.string(), start_time: z.string(), end_time: z.string(),
    starts_at: z.string(), ends_at: z.string(), timezone: z.string()
});
export const upcomingHandoverShiftSchema = handoverShiftSchema.extend({
    recipient_member_id: z.string(), employee_id: z.string(), scheduled_at: z.string(), assignment_id: z.string(), schedule_id: z.string()
});
export const handoverSourceSchema = z.object({ project_id: z.string(), project_name: z.string(), source_id: z.string(),
    source_type: z.string(), source_label: z.string(), source_excerpt: z.string(), source_version: z.number(), source_path: z.string() }).passthrough();
export const shiftHandoverResultSchema = z.object({
    id: z.string().min(1), rule_id: z.string(), title: z.string(), status: z.enum(["succeeded", "failed"]),
    change_kind: z.enum(["initial", "updated", "removed"]).optional(),
    delivery_revision: z.number().int().positive().optional(), previous_result_id: z.string().nullable().optional(),
    content: z.string().nullable(), error: z.object({ code: z.string().optional(), message: z.string() }).nullable(),
    shift: handoverShiftSchema, tasks: z.array(z.record(z.unknown())), source_references: z.array(handoverSourceSchema),
    scheduled_at: z.string().datetime({ offset: true }), created_at: z.string().datetime({ offset: true }), source_read_at: z.string().datetime({ offset: true }).nullable()
}).superRefine((result, context) => {
    if (result.status === "succeeded" && (!result.content?.trim() || result.error)) {
        context.addIssue({ code: "custom", message: "A successful handover requires content and no error" });
    }
    if (result.status === "failed" && (result.content !== null || result.source_references.length > 0 || !result.error)) {
        context.addIssue({ code: "custom", message: "A failed handover cannot display partial content" });
    }
});
export const shiftHandoverRuleResponseSchema = z.object({ rule: shiftHandoverRuleSchema });
export const shiftHandoverRulesResponseSchema = z.object({ items: z.array(shiftHandoverRuleSchema) });
export const shiftHandoverPreviewResponseSchema = z.object({ shifts: z.array(upcomingHandoverShiftSchema) });
export const shiftHandoverResultsResponseSchema = z.object({ items: z.array(shiftHandoverResultSchema), continuation: z.string().nullable() });
export const shiftHandoverResultResponseSchema = z.object({ result: shiftHandoverResultSchema });
export type ShiftHandoverDefinition = z.infer<typeof shiftHandoverDefinitionSchema>;
export type ShiftHandoverRule = z.infer<typeof shiftHandoverRuleSchema>;
export type UpcomingHandoverShift = z.infer<typeof upcomingHandoverShiftSchema>;
export type ShiftHandoverResult = z.infer<typeof shiftHandoverResultSchema>;
